import { isEmpty } from "lodash-es";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { DetailSection } from "@/components/Section";
import { IJobRequest } from "@/data/job-requests";
import { Flex, IFlexProps } from "@/eworkitComponents";
import { HtmlRenderer, isHtmlEmpty } from "@/eworkitMove/HtmlRenderer";
import { AttachmentsList } from "@/views/components/Attachments";
import { CustomField, CustomFields } from "@/views/components/CustomFields";
import { JobRequestLanguages, JobRequestSkills } from "@/views/components/JobRequests/Partials";

interface IDCCSpecificationProps extends Partial<IFlexProps> {
  jobRequest: IJobRequest;
}

export const DCCSpecification: FC<IDCCSpecificationProps> = ({ jobRequest, ...props }) => {
  const { t } = useTranslation("session");

  const requiredSkillsEmpty = isEmpty(jobRequest.skills.filter(skill => skill.priority === "REQUIRED"));
  const preferredSkillsEmpty = isEmpty(jobRequest.skills.filter(skill => skill.priority === "PREFERRED"));

  return (
    <Flex innerMargin="md" direction="column" {...props}>
      <DetailSection
        isVisible={!requiredSkillsEmpty}
        label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HEADER_REQUIRED_SKILLS")}
        weight="bold">
        <JobRequestSkills
          skills={jobRequest.skills.filter(skill => skill.priority === "REQUIRED")}
          showMore={true}
          visibleSkillsCount={15}
        />
      </DetailSection>

      <DetailSection
        isVisible={!preferredSkillsEmpty}
        label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HEADER_PREFERRED_SKILLS")}
        weight="bold">
        <JobRequestSkills
          skills={jobRequest.skills.filter(skill => skill.priority === "PREFERRED")}
          showMore={true}
          visibleSkillsCount={15}
        />
      </DetailSection>

      <DetailSection
        isVisible={!isEmpty(jobRequest.languages)}
        label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HEADER_LANGUAGES")}
        weight="bold">
        <JobRequestLanguages languages={jobRequest.languages} />
      </DetailSection>

      <DetailSection
        isVisible={!isHtmlEmpty(jobRequest.description)}
        label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HEADER_DESCRIPTION")}
        weight="bold">
        <HtmlRenderer html={jobRequest.description} />
      </DetailSection>

      <DetailSection
        isVisible={!isHtmlEmpty(jobRequest.requirements)}
        label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HEADER_REQUIREMENTS")}
        weight="bold">
        <HtmlRenderer html={jobRequest.requirements} />
      </DetailSection>

      <DetailSection
        isVisible={!isHtmlEmpty(jobRequest.skillRoleDescription)}
        label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HEADER_ROLE_DESCRIPTION")}
        weight="bold">
        <HtmlRenderer html={jobRequest.skillRoleDescription} />
      </DetailSection>

      <DetailSection
        isVisible={!isHtmlEmpty(jobRequest.levelDescription)}
        label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HEADER_LEVEL_DESCRIPTION")}
        weight="bold">
        <HtmlRenderer html={jobRequest.levelDescription} />
      </DetailSection>

      <DetailSection
        isVisible={!isEmpty(jobRequest.attachments)}
        label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HEADER_ATTACHMENTS")}
        weight="bold">
        <AttachmentsList attachments={jobRequest.attachments} padding={[false]} />
      </DetailSection>

      <DetailSection
        isVisible={!(isEmpty(jobRequest.customFields) && isEmpty(jobRequest.businessReferenceId))}
        label={t("TASKS.DESIGNATED_CANDIDATE_CREATE.HEADER_CUSTOM_FIELDS")}
        weight="bold">
        <CustomFields customFields={jobRequest.customFields}>
          {!isEmpty(jobRequest.businessReferenceId) && (
            <CustomField
              customField={{
                name: t("TASKS.DESIGNATED_CANDIDATE_CREATE.REFERENCE_ID"),
                value: jobRequest.businessReferenceId!,
                type: "TEXT",
                order: 0,
              }}
            />
          )}
        </CustomFields>
      </DetailSection>
    </Flex>
  );
};
